import type { ContentId, GameId, MissionId, PlayerId, SessionId, SkillId, StageId } from './ids';
import type { PlayerMode, PlayerRuntimeState } from './players';
import type { PlayerParticipationRecord, SessionInputMode } from './session';

/**
 * Where a mission was when it was interrupted (PRO-03).
 *
 * A five-year-old's tablet gets closed mid-round, and a refresh must not cost them the twelve
 * rounds they already finished. This is the smallest state that answers "carry on from here":
 * the position, the attempt in progress, what has already been scored, and whose one teaching
 * moment has been spent.
 *
 * What is deliberately absent is as load-bearing as what is present. There is no camera state,
 * no timing trace, no per-player score and no response history — a resumed session has to be
 * indistinguishable from an uninterrupted one, and none of those would help it be.
 */

/**
 * The game counter keys that may be persisted inside `MissionScoreSnapshot.detail` (R1-15).
 *
 * A registry, deliberately, in the same spirit as `GOVERNED_RECORDS`: `detail` is open-ended in
 * the SDK contract because a game owns its own currency, but *persisted* open maps accept
 * caller-controlled key text, and D-028 already established that an object key is as much a
 * payload as an object value (`{"data:image/png;base64,…": 1, poseX: 0.42}` is a media marker and
 * a coordinate series). A module adding a counter it wants to survive a refresh adds it here,
 * which is a visible, reviewable diff rather than a silent widening of what storage will accept.
 *
 * A counter that is not listed still works at runtime; it simply does not survive an interruption.
 */
export const MISSION_SCORE_DETAIL_KEYS = [
  // Spider Math's shard split and badge signals (D-038(4)).
  'shards',
  'bonusShards',
  'firstAttemptRounds',
  'retryRounds',
  'assistedRounds',
] as const;

/** The scoring state, flattened to the fields the persistence allowlist will rebuild. */
export interface MissionScoreSnapshot {
  teamScore: number;
  roundsScored: number;
  /** Per-player participation counts. Never a per-player score (architecture §12). */
  participation: Record<PlayerId, number>;
  /** Rounds already folded into the score, so a resume cannot score one twice. */
  appliedEventIds: string[];
  /** Game-specific counters, e.g. Spider Math's shard split. */
  detail?: Record<string, number>;
}

/**
 * The parent-summary counters earned so far.
 *
 * Without these a resumed session would report only the rounds played after the interruption —
 * a parent would be told their child attempted three questions on the evening they attempted
 * fourteen. The metric names are the canonical ones (D-007).
 */
export interface MissionProgressCounters {
  questionsAttempted: number;
  questionsCompleted: number;
  retryCount: number;
  teachingMomentCount: number;
  /** Neutral per-player counts. Never a per-player score or an ordering. */
  participation: PlayerParticipationRecord[];
}

export interface MissionProgressSnapshot {
  sessionId: SessionId;
  gameId: GameId;
  missionId: MissionId;
  playerMode: PlayerMode;
  inputMode: SessionInputMode;
  players: PlayerRuntimeState[];
  /** The stage the interrupted round belongs to. */
  stageId: StageId;
  /** Scored-round index across the whole mission, zero-based. */
  roundIndex: number;
  /** Attempt in progress when the session stopped. 1 for a fresh round. */
  attemptNumber: number;
  /** True if the round had already fallen back to the easier variant of its slot. */
  onSimplified: boolean;
  /**
   * Whether a child had already answered this round wrongly when the session stopped (R2-3).
   *
   * It decides what a *later* move-on means. A round nobody answered is a non-response and is
   * recorded as `skipped`; a round somebody got wrong and a grown-up then skipped keeps that
   * answer's `incorrect` evidence, because the evidence came from the child's own answer and not
   * from the skip (D-041(4)). Without this in the snapshot, a refresh between the wrong answer
   * and the skip quietly rewrote the first into the second.
   */
  sawIncorrectAnswer: boolean;
  /**
   * Who has actually responded in this round so far.
   *
   * A neutral list of who was seen, never an ordering and never a per-player score
   * (architecture §12). Evidence is attributed to the children who answered; losing it on a
   * refresh would spread one child's answer across both players of a cooperative sitting.
   */
  observedPlayerIds: PlayerId[];
  /**
   * The item the child was actually looking at, when it is not the position's own item.
   *
   * A post-teaching confirmation question is chosen by the *teaching moment*, so it cannot be
   * re-derived from the position alone — and resuming into a different question than the one on
   * screen would leave the round's evidence describing something nobody saw (R1-3).
   */
  contentId?: ContentId;
  /** Content already resolved, so a replay does not repeat it. */
  completedContentIds: ContentId[];
  /**
   * Whether this sitting is playing the curated first-playthrough sequence (D-037(1), R1-8).
   *
   * The launch config is session-only and a refresh recomputes it as `true`, so without this a
   * refreshed replay would resume into the *curated* sequence — different questions, none of them
   * matching the completed replay ids, so every position would be served again on top of a
   * restored round index and score.
   */
  useCuratedSequence: boolean;
  score: MissionScoreSnapshot;
  progress: MissionProgressCounters;
  /**
   * How many pieces of evidence this session has recorded per skill so far (R1-10).
   *
   * `SkillEvidence` is durable per round but carries no session id, so a completed session cannot
   * find the evidence an earlier sitting of itself wrote. Without these counts a resumed summary
   * reports fourteen questions and lists only the skills practised after the interruption.
   */
  skillPractice: Record<SkillId, number>;
  /** Players whose one full teaching moment is spent (MVP notes §11). */
  teachingConsumedPlayerIds: PlayerId[];
  /**
   * How many times a grown-up used a mid-mission control in this sitting (PS6-9, P6R11-1).
   *
   * The count is an observation instrument for the playtest sheet, and every other fact about a
   * sitting survives a refresh — so a tally that did not made a resumed evening's record report only
   * the help given after the reload. It rides this snapshot because this snapshot *is* the sitting:
   * it is restored when the same sitting resumes and disappears with it when a new one starts.
   *
   * Optional, in the ratified shape (PS5-5): absent means "this build, or this sitting, did not
   * count" — never zero. There is exactly one place that counts (`noteParentOverride` on the stage);
   * this field is a projection of that counter, never a second one.
   *
   * An aggregate, and only ever an aggregate. No child's name is anywhere near it, by construction:
   * there is no per-player breakdown to add and nothing downstream that could attribute it.
   */
  parentOverridesUsed?: number;
  /** ISO-8601 timestamps. */
  startedAt: string;
  updatedAt: string;
}
